import { useMemo, useState } from "react";
import { apiService } from "../../services/apiService";
import { errorNotify } from "../../utils/toast";
import { prepareFuelListingPayload } from "./FuelListing";

const EXPORT_CONFIG = {
  pdf: {
    request: (payload) => apiService.getFuelLogsListingPdf(payload),
    fileName: "fuel-logs-listing.pdf",
    fileType: "PDF",
  },
  excel: {
    request: (payload) => apiService.getFuelLogsListingExcel(payload),
    fileName: "fuel-logs-listing.xlsx",
    fileType: "Excel",
  },
};

export const useFuelListingExport = (searchText, searchFilters) => {
  const [loadingType, setLoadingType] = useState(null);

  const hasActiveFilters = useMemo(() => {
    const payload = prepareFuelListingPayload(searchFilters, searchText);
    return Boolean(
      payload.search_text ||
      Object.keys(payload).some((key) => key !== "search_text"),
    );
  }, [searchFilters, searchText]);

  const handleExport = async (type) => {
    const config = EXPORT_CONFIG[type];
    if (!config || !hasActiveFilters || loadingType) return;

    setLoadingType(type);
    try {
      const payload = prepareFuelListingPayload(searchFilters, searchText);
      const response = await config.request(payload);
      const responseData = response?.data ?? response;
      const filePath =
        responseData?.url ||
        responseData?.file ||
        (typeof responseData === "string" ? responseData : null);

      if (!filePath) {
        throw new Error(`${config.fileType} was generated but no file was returned`);
      }

      const fileUrl = /^https?:\/\//i.test(filePath)
        ? filePath
        : `${process.env.REACT_APP_API_FILES_URL}${filePath}`;
      const link = document.createElement("a");
      link.href = fileUrl;
      link.target = "_blank";
      link.setAttribute("download", config.fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      const message = typeof error === "string" ? error : error?.message;
      errorNotify(message || `Failed to download fuel logs ${config.fileType}`);
    } finally {
      setLoadingType(null);
    }
  };

  return {
    hasActiveFilters,
    exportLoading: Boolean(loadingType),
    pdfExportLoading: loadingType === "pdf",
    excelExportLoading: loadingType === "excel",
    handleExportPdf: () => handleExport("pdf"),
    handleExportExcel: () => handleExport("excel"),
  };
};
